import React from "react";
import {
  Button,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";

const useStyles = makeStyles({
  button: {
    boxShadow: tokens.shadow16Brand,
    minWidth: "180px",
    ...shorthands.borderStyle("none"),
    ...shorthands.borderRadius(tokens.spacingHorizontalXL),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalXXL),
    ...shorthands.margin(tokens.spacingHorizontalXXXL, 0),
  },
  primary: {
    backgroundColor: tokens.colorBrandBackground,
    color: "#ffffff",

    ":hover": {
      color: "#dfdfdfff",
      backgroundColor: tokens.colorBrandBackgroundHover,
    },

    ":active": {
      color: "#dfdfdfff !important",
      backgroundColor: `${tokens.colorBrandBackgroundPressed} !important`,
    },

    ":disabled": {
      color: tokens.colorNeutralStrokeDisabled,
      backgroundColor: tokens.colorNeutralForegroundDisabled,

      ":hover": {
        color: tokens.colorNeutralForegroundDisabled,
        backgroundColor: tokens.colorNeutralStrokeDisabled,
      },
    },
  },
  secondary: {
    backgroundColor: "#ffffff",
    color: tokens.colorBrandBackground,

    ":hover": {
      color: tokens.colorBrandBackground,
      backgroundColor: tokens.colorBrandBackground2Hover,
    },

    ":active": {
      color: `${tokens.colorBrandBackground} !important`,
      backgroundColor: `${tokens.colorBrandBackground2Pressed} !important`,
    },
  },
  alternative: {
    backgroundColor: "#FFB327",
    color: "#ffffff",

    ":hover": {
      color: "#dfdfdfff",
      backgroundColor: "#d38b06ff",
    },

    ":active": {
      color: "#dfdfdf !important",
      backgroundColor: "#b67805ff !important",
    },
  },
  subtle: {
    boxShadow: "none",

    backgroundColor: tokens.colorTransparentBackground,
    color: tokens.colorBrandBackground,

    ":hover": {
      color: tokens.colorBrandBackgroundHover,
      backgroundColor: tokens.colorTransparentBackground,
    },

    ":active": {
      color: `${tokens.colorBrandBackgroundPressed} !important`,
      backgroundColor: `${tokens.colorTransparentBackground} !important`,
    },
  },
});

type CtaButtonProps = {
  buttonText: string | JSX.Element;
  color?: "primary" | "secondary" | "alternative" | "subtle";
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
  className?: string;
  icon?: JSX.Element;
  disabled?: boolean;
};

export const CtaButton = ({
  buttonText,
  color = "primary",
  type,
  onClick,
  className,
  icon,
  disabled,
}: CtaButtonProps) => {
  const classes = useStyles();

  return (
    <Button
      size="large"
      type={type ?? "button"}
      onClick={onClick}
      className={mergeClasses(classes.button, classes[color], className ?? "")}
      icon={icon}
      disabled={disabled}
    >
      {buttonText}
    </Button>
  );
};
